import { countries } from "../constants";

const text = (value) => typeof value === "string" ? value.trim() : "";
const upper = (value) => text(value).toUpperCase();

// ISO 3166 alpha-3 -> alpha-2 (the SDK returns alpha-3, e.g. nationality "PAK").
const ALPHA3_TO_ALPHA2 = {
  AFG: "AF", AGO: "AO", ALB: "AL", AND: "AD", ARE: "AE", ARG: "AR", ARM: "AM", ATG: "AG",
  AUS: "AU", AUT: "AT", AZE: "AZ", BDI: "BI", BEL: "BE", BEN: "BJ", BFA: "BF", BGD: "BD",
  BGR: "BG", BHR: "BH", BHS: "BS", BIH: "BA", BLR: "BY", BLZ: "BZ", BOL: "BO", BRA: "BR",
  BRB: "BB", BRN: "BN", BTN: "BT", BWA: "BW", CAF: "CF", CAN: "CA", CHE: "CH", CHL: "CL",
  CHN: "CN", CIV: "CI", CMR: "CM", COD: "CD", COG: "CG", COL: "CO", COM: "KM", CPV: "CV",
  CRI: "CR", CUB: "CU", CYP: "CY", CZE: "CZ", DEU: "DE", DJI: "DJ", DMA: "DM", DNK: "DK",
  DOM: "DO", DZA: "DZ", ECU: "EC", EGY: "EG", ERI: "ER", ESP: "ES", EST: "EE", ETH: "ET",
  FIN: "FI", FJI: "FJ", FRA: "FR", FSM: "FM", GAB: "GA", GBR: "GB", GEO: "GE", GHA: "GH",
  GIN: "GN", GMB: "GM", GNB: "GW", GNQ: "GQ", GRC: "GR", GRD: "GD", GTM: "GT", GUY: "GY",
  HND: "HN", HRV: "HR", HTI: "HT", HUN: "HU", IDN: "ID", IND: "IN", IRL: "IE", IRN: "IR",
  IRQ: "IQ", ISL: "IS", ISR: "IL", ITA: "IT", JAM: "JM", JOR: "JO", JPN: "JP", KAZ: "KZ",
  KEN: "KE", KGZ: "KG", KHM: "KH", KIR: "KI", KNA: "KN", KOR: "KR", KWT: "KW", LAO: "LA",
  LBN: "LB", LBR: "LR", LBY: "LY", LCA: "LC", LIE: "LI", LKA: "LK", LSO: "LS", LTU: "LT",
  LUX: "LU", LVA: "LV", MAR: "MA", MCO: "MC", MDA: "MD", MDG: "MG", MDV: "MV", MEX: "MX",
  MHL: "MH", MKD: "MK", MLI: "ML", MLT: "MT", MMR: "MM", MNE: "ME", MNG: "MN", MOZ: "MZ",
  MRT: "MR", MUS: "MU", MWI: "MW", MYS: "MY", NAM: "NA", NER: "NE", NGA: "NG", NIC: "NI",
  NLD: "NL", NOR: "NO", NPL: "NP", NRU: "NR", NZL: "NZ", OMN: "OM", PAK: "PK", PAN: "PA",
  PER: "PE", PHL: "PH", PLW: "PW", PNG: "PG", POL: "PL", PRK: "KP", PRT: "PT", PRY: "PY",
  PSE: "PS", QAT: "QA", ROU: "RO", RUS: "RU", RWA: "RW", SAU: "SA", SDN: "SD", SEN: "SN",
  SGP: "SG", SLB: "SB", SLE: "SL", SLV: "SV", SMR: "SM", SOM: "SO", SRB: "RS", SSD: "SS",
  STP: "ST", SUR: "SR", SVK: "SK", SVN: "SI", SWE: "SE", SWZ: "SZ", SYC: "SC", SYR: "SY",
  TCD: "TD", TGO: "TG", THA: "TH", TJK: "TJ", TKM: "TM", TLS: "TL", TON: "TO", TTO: "TT",
  TUN: "TN", TUR: "TR", TUV: "TV", TZA: "TZ", UGA: "UG", UKR: "UA", URY: "UY", USA: "US",
  UZB: "UZ", VAT: "VA", VCT: "VC", VEN: "VE", VNM: "VN", VUT: "VU", WSM: "WS", YEM: "YE",
  ZAF: "ZA", ZMB: "ZM", ZWE: "ZW",
};

export function readSdkData(response) {
  try {
    const bvr = typeof response?.bvrData === "string"
      ? JSON.parse(response.bvrData) : response?.bvrData;
    return bvr && typeof bvr.data === "object" && bvr.data !== null ? bvr : null;
  } catch {
    return null;
  }
}

export function isValidDealerSdkResponse(response) {
  const bvr = readSdkData(response);
  return response?.success === true && (
    upper(bvr?.type) === "PASSPORT" ||
    (upper(bvr?.type) === "MYKAD" && bvr.data.fingerprintsLoaded === true)
  );
}

export function normalizeSdkDate(value) {
  let date = text(value);
  if (/^\d{8}$/.test(date)) date = `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`;
  else if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
    const [day, month, year] = date.split("/");
    date = `${year}-${month}-${day}`;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return "";
  const parsed = new Date(`${date}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date ? date : "";
}

export function mapDealerSdkFields(response, states = []) {
  const bvr = readSdkData(response);
  if (!bvr) return {};
  const data = bvr.data;
  const passport = upper(bvr.type) === "PASSPORT";
  const name = text(data.name).replace(/\s+/g, " ");
  const gender = upper(data.gender);
  const state = states.find((item) =>
    upper(item.name) === upper(data.state) || String(item.code).toUpperCase() === upper(data.state));
  // Passport: the SDK returns the issuing country in `nationality` (alpha-3, e.g. "PAK");
  // `issuingCountry` is null. MyKad is always issued by Malaysia.
  const issuingCountry = passport
    ? [data.issuingCountry, data.issuingCountryId, data.nationality].map((value) => {
        const v = upper(value);
        if (!v) return undefined;
        const code = ALPHA3_TO_ALPHA2[v] || v;
        return countries.find((item) => item.code === code || upper(item.name) === v);
      }).find(Boolean)
    : countries.find((item) => item.code === "MY");
  const fields = {
    identity_type: passport ? (upper(data.documentType) || "PASSPORT") : "MYKAD",
    first_name: passport ? text(data.firstName) : name.split(" ")[0],
    full_name: passport ? [text(data.firstName), text(data.lastName)].filter(Boolean).join(" ") : name,
    identity_no: text(data.idNumber),
    date_of_birth: normalizeSdkDate(data.birthDate),
    gender: ["MALE", "M", "L", "LELAKI"].includes(gender) ? "Male"
      : ["FEMALE", "F", "P", "PEREMPUAN"].includes(gender) ? "Female" : "",
    address1: text(passport ? data.address : data.address1),
    address2: passport ? text(data.address2) : [text(data.address2), text(data.address3)].filter(Boolean).join(", "),
    city: text(data.city),
    state: state ? String(state.code) : "",
    postal_code: text(data.postcode),
    issuance_country: issuingCountry?.code || "",
    ...(passport ? { identity_expiry_date: normalizeSdkDate(data.expiryDate) } : {}),
  };
  return Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== ""));
}

export function dealerSdkPayload(response) {
  const bvr = readSdkData(response);
  return {
    reference_no: response.refNo,
    bvr_id: response.bvrId,
    ...(upper(bvr?.type) === "PASSPORT"
      ? { passport: bvr.data.documentFrontUrl || "" }
      : {
        mykad_front: text(bvr?.data?.documentFrontUrl) || null,
        mykad_back: text(bvr?.data?.documentBackUrl) || null,
      }),
  };
}
